import React, { useState, useEffect, useRef } from 'react';
import { API, C } from '../../constants';

// El backend guarda un subtítulo por (video, idioma): subir otro del mismo idioma lo reemplaza.
const LANGS = [
  { code:'es', label:'Español' },
  { code:'en', label:'English' },
  { code:'pt', label:'Português' },
  { code:'fr', label:'Français' },
];

export default function AdminSubtitlesTab({ token, media, mediaInfo }) {
  const [selectedFile, setSelectedFile] = useState('');
  const [subtitles, setSubtitles]       = useState([]);
  const [uploading, setUploading]       = useState(false);
  const [msg, setMsg]                   = useState('');
  const [lang, setLang]                 = useState('es');
  const fileRef = useRef(null);
  const reqFileRef = useRef('');

  const headers = { Authorization: `Bearer ${token}` };

  // Al cambiar rápido de video, una respuesta lenta del anterior podía pisar
  // la lista del actual; se descarta si ya no corresponde.
  const loadSubs = (file) => {
    reqFileRef.current = file;
    if (!file) { setSubtitles([]); return; }
    fetch(`${API}/api/subtitles?file=${encodeURIComponent(file)}`, { headers })
      .then(r => r.json())
      .then(d => { if (reqFileRef.current === file) setSubtitles(Array.isArray(d) ? d : []); })
      .catch(() => { if (reqFileRef.current === file) setSubtitles([]); });
  };

  useEffect(() => { loadSubs(selectedFile); setMsg(''); }, [selectedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { setMsg('Selecciona un archivo .srt o .vtt'); return; }
    if (!selectedFile) { setMsg('Selecciona el video al que pertenece'); return; }
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['srt','vtt'].includes(ext)) { setMsg('Solo se aceptan archivos .srt o .vtt'); return; }
    setUploading(true); setMsg('');
    // Los nombres de campo deben coincidir con handleAdminUploadSubtitle.
    const form = new FormData();
    form.append('subtitle', f);
    form.append('file_name', selectedFile);
    form.append('language', lang);
    form.append('label', LANGS.find(l => l.code === lang)?.label || lang);
    try {
      const res = await fetch(`${API}/admin/subtitles`, { method:'POST', headers, body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(data.error || 'Error subiendo subtítulos'); return; }
    } catch { setMsg('Error de conexión'); return; }
    finally { setUploading(false); }
    setMsg('✓ Subtítulos subidos correctamente');
    if (fileRef.current) fileRef.current.value = '';
    loadSubs(selectedFile);
  };

  const deleteSub = async (id) => {
    if (!window.confirm('¿Eliminar este subtítulo?')) return;
    const res = await fetch(`${API}/admin/subtitles/${id}`, { method:'DELETE', headers }).catch(() => null);
    if (!res?.ok) { const d = await res?.json().catch(() => ({})); setMsg(d?.error || 'Error eliminando subtítulo'); }
    else setMsg('✓ Subtítulo eliminado');
    loadSubs(selectedFile);
  };

  // El título de media_info es más reconocible que el nombre del archivo.
  const label   = m => mediaInfo?.[m.file]?.title ? `${mediaInfo[m.file].title} — ${m.file}` : m.name;
  const byLabel = (a, b) => label(a).localeCompare(label(b));
  const movies  = (media || []).filter(m => m.type === 'movie').sort(byLabel);
  const series  = (media || []).filter(m => m.type === 'series').sort(byLabel);

  return (
    <div>
      <h3 style={{ color:C.text, fontSize:'14px', fontWeight:'600', marginBottom:'16px', fontFamily:'Inter, sans-serif' }}>Gestión de subtítulos</h3>

      <div style={{ marginBottom:'18px' }}>
        <p style={{ color:C.textMuted, fontSize:'12px', marginBottom:'8px', fontFamily:'Inter, sans-serif' }}>Video al que pertenecen los subtítulos</p>
        <select value={selectedFile} onChange={e => setSelectedFile(e.target.value)}
          style={{ width:'100%', padding:'11px 14px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.accentBorder}`, color: selectedFile ? C.text : C.textMuted, fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:'Inter, sans-serif', cursor:'pointer' }}>
          <option value=''>— Seleccionar video —</option>
          {movies.length > 0 && (
            <optgroup label='Películas'>
              {movies.map(m => <option key={m.file} value={m.file}>{label(m)}</option>)}
            </optgroup>
          )}
          {series.length > 0 && (
            <optgroup label='Series (archivos)'>
              {series.map(m => <option key={m.file} value={m.file}>{label(m)}</option>)}
            </optgroup>
          )}
        </select>
      </div>

      <div style={{ background:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'18px', marginBottom:'18px' }}>
        <p style={{ color:C.textMuted, fontSize:'12px', marginBottom:'12px', fontFamily:'Inter, sans-serif' }}>Subir archivo (.srt o .vtt)</p>
        <select value={lang} onChange={e => setLang(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.accentBorder}`, color:C.text, fontSize:'13px', marginBottom:'12px', fontFamily:'Inter, sans-serif', cursor:'pointer' }}>
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        {subtitles.some(s => s.language === lang) && <p style={{ color:C.gold, fontSize:'11px', marginBottom:'10px', fontFamily:'Inter, sans-serif' }}>Ya existe un subtítulo en este idioma; se reemplazará.</p>}
        <input ref={fileRef} type='file' accept='.srt,.vtt'
          style={{ color:C.text, fontSize:'13px', marginBottom:'12px', display:'block', fontFamily:'Inter, sans-serif' }} />
        <button onClick={upload} disabled={uploading}
          style={{ padding:'9px 20px', background: uploading ? C.textDim : `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border:'none', color:'#fff', borderRadius:'8px', cursor: uploading ? 'not-allowed' : 'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'Inter, sans-serif' }}>
          {uploading ? 'Subiendo...' : '⬆ Subir subtítulos'}
        </button>
        {msg && <p style={{ marginTop:'10px', color: msg.includes('✓') ? C.success : C.danger, fontSize:'12px', fontFamily:'Inter, sans-serif' }}>{msg}</p>}
      </div>

      {selectedFile && (
        <div>
          <h4 style={{ color:C.text, fontSize:'13px', fontWeight:'600', marginBottom:'10px', fontFamily:'Inter, sans-serif' }}>
            Subtítulos ({subtitles.length})
          </h4>
          {subtitles.length === 0 ? (
            <p style={{ color:C.textDim, fontSize:'13px', fontFamily:'Inter, sans-serif' }}>Sin subtítulos para este video.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {subtitles.map(s => (
                <div key={s.id} style={{ padding:'10px 14px', background:C.bgCard, borderRadius:'8px', border:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
                  <div>
                    <p style={{ color:C.text, fontSize:'12px', fontWeight:'500', fontFamily:'Inter, sans-serif' }}>{s.label || s.sub_file}</p>
                    <p style={{ color:C.textMuted, fontSize:'11px', marginTop:'2px', fontFamily:'Inter, sans-serif' }}>{s.language} · {s.sub_file}</p>
                  </div>
                  <button onClick={() => deleteSub(s.id)}
                    style={{ background:C.dangerDim, border:`1px solid rgba(192,57,43,0.3)`, color:C.danger, padding:'5px 10px', borderRadius:'6px', cursor:'pointer', fontSize:'11px', flexShrink:0 }}>
                    Eliminar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
